/**
 * ResultsPageHeader Component
 *
 * Displays the results page header including:
 * - Taxonomy breadcrumbs with navigation
 * - Page title with dynamic gender prefix
 * - "Showing results for" subtitle
 */

import React from "react";

import Image from "next/image";

import { colors } from "@/styles/design-tokens";

import type { ShopperFilters } from "@/lib/shopper-search";

/**
 * Gets gender prefix for page title based on search params facets
 */
function getGenderPrefix(params: ShopperFilters | null): string {
  if (!params) return "";
  const facets = (
    params as unknown as {
      facets?: Array<{ name: string; values: string[] }>;
    }
  ).facets;
  if (!facets || !Array.isArray(facets)) return "";

  // Check if we're in kids clothing
  const depth0Facet = facets.find((f) => f.name === "category_path.depth_0");
  const isKidsClothing = depth0Facet?.values?.[0]?.toLowerCase().includes("kid");

  const genderFacet = facets.find((f) => f.name === "gender");
  if (genderFacet && genderFacet.values?.[0]) {
    const gender = genderFacet.values[0];
    if (isKidsClothing) {
      return gender === "men" ? "Boy's " : gender === "women" ? "Girl's " : "";
    }
    return gender === "men" ? "Men's " : gender === "women" ? "Women's " : "";
  }
  return "";
}

/**
 * Computes the page title based on search state
 */
function computePageTitle(
  isSearching: boolean,
  hasResults: boolean,
  breadcrumbPath: string[] | null,
  searchTextQuery: string | null,
  searchParams: ShopperFilters | null
): string {
  if (isSearching || !hasResults) {
    return "Search Results";
  }

  if (breadcrumbPath && breadcrumbPath.length > 0) {
    const categoryName = breadcrumbPath[breadcrumbPath.length - 1] || "Results";

    // Don't add gender prefix for top-level gender categories
    // (e.g., don't show "Men's Menswear", just "Menswear")
    const isTopLevelGenderCategory = breadcrumbPath.length <= 1;
    const genderPrefix = isTopLevelGenderCategory ? "" : getGenderPrefix(searchParams);
    return `${genderPrefix}${categoryName}`;
  } else if (searchTextQuery) {
    return searchTextQuery.charAt(0).toUpperCase() + searchTextQuery.slice(1);
  }

  return "Search Results";
}

export interface ResultsPageHeaderProps {
  /** Breadcrumb path segments (taxonomy navigation) */
  breadcrumbPath: string[] | null;
  /** The search text query */
  searchTextQuery: string | null;
  /** The internal search params */
  searchParams: ShopperFilters | null;
  /** Whether search results exist */
  hasResults: boolean;
  /** Whether search is in progress */
  isSearching: boolean;
  /** Callback when a breadcrumb segment is clicked */
  onCategoryNavigate: (path: string) => void;
  /** Whether breadcrumb navigation is enabled */
  isCategoryNavigationEnabled?: boolean;
}

/**
 * Results page header with breadcrumbs and title
 */
export function ResultsPageHeader({
  breadcrumbPath,
  searchTextQuery,
  searchParams,
  hasResults,
  isSearching,
  onCategoryNavigate,
  isCategoryNavigationEnabled = true,
}: ResultsPageHeaderProps): React.ReactElement {
  const pageTitle = computePageTitle(
    isSearching,
    hasResults,
    breadcrumbPath,
    searchTextQuery,
    searchParams
  );

  return (
    <div className="flex flex-col gap-3 pt-4 lg:gap-4 lg:pt-8">
      {/* Taxonomy Breadcrumbs - Figma Node: 51:7113 */}
      {breadcrumbPath && breadcrumbPath.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs lg:gap-[7px] lg:text-sm">
          {breadcrumbPath.map((segment: string, index: number) => {
            const isLast = index === breadcrumbPath.length - 1;
            const pathUpToHere = breadcrumbPath.slice(0, index + 1).join(" > ");

            return (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Image
                    src="/icons/chevron.svg"
                    alt=""
                    width={14}
                    height={14}
                    style={{
                      flexShrink: 0,
                      opacity: 0.6,
                    }}
                  />
                )}
                {isLast ? (
                  <span className="text-action-muted text-ink">{segment}</span>
                ) : isCategoryNavigationEnabled ? (
                  <button
                    type="button"
                    onClick={() => onCategoryNavigate(pathUpToHere)}
                    className="text-action-muted text-ink-secondary hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    {segment}
                  </button>
                ) : (
                  <span className="text-action-muted text-ink-secondary">{segment}</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Results Page Title - Figma Node: 51:6096 */}
      <div className="flex flex-col gap-3 lg:gap-3.5">
        {/* Results Heading - Figma Node: 51:6097 / Loading: 895:21275 */}
        <h1
          className="text-lg font-semibold lg:text-[21px] lg:leading-[28px]"
          style={{ color: colors.text.black }}
        >
          {pageTitle}
        </h1>

        {/* Results Subheading - Figma Node: 51:6099 - Hidden during loading */}
        {!isSearching && hasResults && searchTextQuery && (
          <p className="m-0 text-xs lg:text-sm">
            <span className="text-action-muted text-ink-secondary">Showing results for: </span>
            <span className="text-action text-ink-secondary">&quot;{searchTextQuery}&quot;</span>
          </p>
        )}
      </div>
    </div>
  );
}
